import { globalConfirm } from '../components/ConfirmDialog';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppShell from '../components/layout/AppShell';
import { CollapsibleSection } from '../components/layout/SharedComponents';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import api, { getApiErrorMessage } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTheme, PREDEFINED_THEMES as THEMES } from '../contexts/ThemeContext';
import { useExpenditureLabels } from '../contexts/ExpenditureLabelsContext';
import TypeAheadLookup from '../components/TypeAheadLookup';
import { toast } from 'sonner';
import { User, Globe, Lock, Save, Camera, Trash2, Palette, Check, Building2, Shield } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LANGUAGES = [
  { value: 'en', label: 'English (UK)', flag: '🇬🇧' },
  { value: 'en-US', label: 'English (US)', flag: '🇺🇸' },
  { value: 'fr', label: 'Français', flag: '🇫🇷' },
  { value: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { value: 'es', label: 'Español', flag: '🇪🇸' },
  { value: 'ar', label: 'العربية', flag: '🇸🇦' },
  { value: 'ja', label: '日本語', flag: '🇯🇵' },
];

export default function ProfilePage() {
  const { user, loadUser, systemSettings } = useAuth();
  const { theme: activeTheme, setTheme } = useTheme();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: '', last_name: '', job_title: '',
    telephone: '', mobile: '', extension: '',
    language: 'en',
  });
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [changingPw, setChangingPw] = useState(false);
  const photoInputRef = useRef(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);


  useEffect(() => {
    if (user) {
      setForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        job_title: user.job_title || '',
        telephone: user.telephone || '',
        mobile: user.mobile || '',
        extension: user.extension || '',
        language: user.language || 'en',
        is_out_of_office: user.is_out_of_office || false,
        delegate_user_id: user.delegate_user_id || '',
        delegate_name: user.delegate_name || '',
      });
      // Try to load existing photo
      const url = `${BACKEND_URL}/api/users/${user.user_id}/profile-photo?t=${Date.now()}`;
      fetch(url).then(r => { if (r.ok) setPhotoPreview(url); }).catch(() => {});
    }
  }, [user]);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const uploadPhotoFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please drop an image file'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('Image must be 2 MB or smaller'); return; }
    // Show local preview immediately so user sees feedback
    const reader = new FileReader();
    reader.onload = ev => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
    // Upload — do NOT set Content-Type manually; axios sets it with the
    // correct multipart boundary automatically when it receives a FormData object
    setPhotoLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/api/users/${user.user_id}/profile-photo`, fd);
      // Switch preview to server URL with a fresh cache-bust version
      const newVersion = Date.now();
      const serverUrl = `${BACKEND_URL}/api/users/${user.user_id}/profile-photo?v=${newVersion}`;
      setPhotoPreview(serverUrl);
      // Tell AppShell to refresh the topbar avatar immediately
      window.dispatchEvent(new CustomEvent('profile-photo-updated', { detail: { version: newVersion } }));
      toast.success('Profile photo saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed — please try again');
      setPhotoPreview(null); // revert preview on failure
    }
    setPhotoLoading(false);
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    await uploadPhotoFile(file);
    e.target.value = '';
  };

  const handlePhotoDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    await uploadPhotoFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handlePhotoDelete = async () => {
    if (!await globalConfirm('Remove your profile photo?')) return;
    try {
      await api.delete(`/api/users/${user.user_id}/profile-photo`);
      setPhotoPreview(null);
      // Tell AppShell to revert to initials immediately
      window.dispatchEvent(new CustomEvent('profile-photo-updated', { detail: { version: 0 } }));
      toast.success('Profile photo removed');
    } catch (err) { if (err?.code !== 'ERR_CANCELED' && err?.response?.status !== 401 && err?.response?.status !== 403) toast.error('Failed to remove'); }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put(`/api/users/${user.user_id}`, form);
      // Apply language change immediately
      if (form.language !== i18n.language) {
        i18n.changeLanguage(form.language);
        localStorage.setItem('i18nextLng', form.language);
      }
      toast.success('Profile saved. Language will apply immediately.');
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Save failed'));
    }
    setSaving(false);
  };

  const changePassword = async () => {
    if (!passwords.current) { toast.error('Enter your current password'); return; }
    if (passwords.new.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (passwords.new !== passwords.confirm) { toast.error('New passwords do not match'); return; }
    setChangingPw(true);
    try {
      await api.post('/api/auth/change-password', {
        current_password: passwords.current,
        new_password: passwords.new,
      });
      toast.success('Password changed successfully');
      setPasswords({ current: '', new: '', confirm: '' });
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Password change failed'));
    }
    setChangingPw(false);
  };

  const selectedLang = LANGUAGES.find(l => l.value === form.language);

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div
            className={`flex items-center gap-4 rounded-xl border-2 border-dashed px-4 py-3 transition-all cursor-pointer ${isDragging ? 'border-primary bg-primary/10 ring-2 ring-primary/30' : 'border-border hover:border-primary/50 hover:bg-muted/30'}`}
            onDragOver={handleDragOver}
            onDragEnter={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handlePhotoDrop}
            onClick={(e) => {
              // Only trigger file picker when clicking the wrapper itself (not buttons inside)
              if (e.target === e.currentTarget || e.target.tagName === 'DIV' || e.target.tagName === 'H1' || e.target.tagName === 'P' || e.target.tagName === 'SPAN') {
                photoInputRef.current?.click();
              }
            }}
            data-testid="profile-photo-dropzone"
            role="button"
            tabIndex={0}
            aria-label="Drag and drop or click to upload profile photo"
          >
            {/* Profile photo with upload overlay */}
            <div className="relative group flex-shrink-0 pointer-events-none">
              {photoPreview ? (
                <img src={photoPreview} alt="Profile"
                  className="w-16 h-16 rounded-full object-cover border-2 border-border" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center text-white text-xl font-bold border-2 border-border">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </div>
              )}
              {/* Upload overlay on hover */}
              <div
                className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                aria-hidden="true"
              >
                <Camera className="h-5 w-5 text-white" />
              </div>
              <input ref={photoInputRef} type="file" accept="image/*" className="hidden"
                onChange={handlePhotoChange} aria-label="Upload profile photo" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold truncate" style={{ fontFamily: 'Space Grotesk' }}>
                {user?.first_name} {user?.last_name}
              </h1>
              <p className="text-sm text-muted-foreground truncate">{user?.job_title} &mdash; {user?.email}</p>
              <div className="flex items-center gap-2 mt-1.5">
                <span className={`text-xs flex items-center gap-1 ${isDragging ? 'text-primary font-medium' : 'text-primary'}`}>
                  <Camera className="h-3 w-3" />
                  {isDragging ? 'Drop image to upload' : (photoPreview ? 'Drag a new photo here, or click to browse' : 'Drag a photo here, or click to browse')}
                </span>
                {photoPreview && (
                  <>
                    <span className="text-border text-xs">·</span>
                    <button type="button" onClick={(e) => { e.stopPropagation(); handlePhotoDelete(); }} className="text-xs text-destructive hover:underline flex items-center gap-1 relative z-10">
                      <Trash2 className="h-3 w-3" /> Remove
                    </button>
                  </>
                )}
                <span className="text-[10px] text-muted-foreground">(max 2 MB)</span>
              </div>
            </div>
          </div>
          <Badge variant="outline" className="capitalize">{user?.user_type}</Badge>
        </div>

        <div className="space-y-4">
          {/* Personal Info */}
          <CollapsibleSection title={t('profile.personalInfo')}>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="fn">{t('profile.firstName')}</Label>
                <Input id="fn" value={form.first_name} onChange={e => f('first_name', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ln">{t('profile.lastName')}</Label>
                <Input id="ln" value={form.last_name} onChange={e => f('last_name', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="jt">{t('profile.jobTitle')}</Label>
                <Input id="jt" value={form.job_title} onChange={e => f('job_title', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ext">{t('profile.extension')}</Label>
                <Input id="ext" value={form.extension} onChange={e => f('extension', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tel">{t('profile.telephone')}</Label>
                <Input id="tel" value={form.telephone} onChange={e => f('telephone', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="mob">{t('profile.mobile')}</Label>
                <Input id="mob" value={form.mobile} onChange={e => f('mobile', e.target.value)} />
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <Button onClick={saveProfile} disabled={saving} data-testid="profile-save-button">
                <Save className="h-4 w-4 mr-1" />
                {saving ? t('profile.saving') : t('profile.saveProfile')}
              </Button>
            </div>
          </CollapsibleSection>

          {/* Language Preference */}
          <CollapsibleSection title={t('profile.languagePref')}>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t('profile.languageDesc')}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {LANGUAGES.map(lang => (
                  <button
                    key={lang.value}
                    onClick={() => f('language', lang.value)}
                    className={`flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${
                      form.language === lang.value
                        ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                        : 'border-border hover:border-muted-foreground hover:bg-accent'
                    }`}
                    aria-pressed={form.language === lang.value}
                  >
                    <span className="text-xl">{lang.flag}</span>
                    <div>
                      <p className="text-sm font-medium">{lang.label}</p>
                      <p className="text-[10px] text-muted-foreground font-mono">{lang.value}</p>
                    </div>
                    {form.language === lang.value && (
                      <div className="ml-auto w-4 h-4 rounded-full bg-primary flex items-center justify-center">
                        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </button>
                ))}
              </div>
              <div className="flex items-center justify-between pt-2">
                <p className="text-xs text-muted-foreground">
                  {t('profile.currentLang')}: <strong>{selectedLang?.flag} {selectedLang?.label}</strong>
                </p>
                <Button onClick={saveProfile} disabled={saving} size="sm">
                  <Globe className="h-4 w-4 mr-1" />
                  {saving ? t('profile.saving') : t('profile.saveLanguage')}
                </Button>
              </div>
            </div>
          </CollapsibleSection>

          {/* Out of Office */}
          <CollapsibleSection title="Out of Office" defaultOpen={form.is_out_of_office}>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">I am currently out of office</p>
                  <p className="text-xs text-muted-foreground">When enabled, your approval responsibilities will be delegated to the person you nominate below.</p>
                </div>
                <Switch checked={form.is_out_of_office} onCheckedChange={v => f('is_out_of_office', v)} data-testid="ooo-toggle" />
              </div>
              {form.is_out_of_office && (
                <div className="space-y-2 pl-1 border-l-2 border-amber-300 ml-1 pl-4">
                  <Label className="text-xs font-semibold">Delegated Authority</Label>
                  <p className="text-xs text-muted-foreground">Select the person who will receive and action your approval requests while you are away.</p>
                  <TypeAheadLookup
                    endpoint="/api/users"
                    valueField="user_id"
                    labelField="first_name"
                    value={form.delegate_user_id}
                    displayValue={form.delegate_name || form.delegate_user_id}
                    placeholder="Search for a delegate..."
                    onChange={(uid, record) => {
                      f('delegate_user_id', uid);
                      f('delegate_name', record ? `${record.first_name || ''} ${record.last_name || ''}`.trim() : '');
                    }}
                  />
                  {form.delegate_name && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-1.5">
                      All approval notifications will be sent to <strong>{form.delegate_name}</strong> until you turn off Out of Office.
                    </p>
                  )}
                </div>
              )}
              <div className="flex justify-end">
                <Button onClick={saveProfile} disabled={saving} size="sm">
                  <Save className="h-4 w-4 mr-1" /> {saving ? t('profile.saving') : 'Save'}
                </Button>
              </div>
            </div>
          </CollapsibleSection>


          {/* My Sites — read only */}
          <MySitesDisplay userId={user?.user_id} />


          {/* Two-Factor Authentication Setup */}
          <TwoFactorSetup />



          {/* Colour Theme */}
          <CollapsibleSection title={t('term.colourTheme')}>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t('term.colourThemeDesc')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {Object.entries(THEMES).map(([key, t]) => {
                  const isActive = activeTheme === key;
                  return (
                    <button
                      key={key}
                      data-testid={`theme-${key}`}
                      onClick={() => setTheme(key)}
                      className={`relative text-left p-3 rounded-xl border transition-all ${
                        isActive
                          ? 'border-primary ring-2 ring-primary/30 bg-primary/5'
                          : 'border-border hover:border-muted-foreground hover:shadow-sm'
                      }`}
                    >
                      {/* Colour preview strip */}
                      <div className="flex gap-1 mb-2.5">
                        {(t.preview || []).map((c, i) => (
                          <div key={i} className="h-5 flex-1 rounded-md" style={{ backgroundColor: c }} />
                        ))}
                      </div>
                      <p className="text-sm font-medium">{t.name}</p>
                      <p className="text-[11px] text-muted-foreground">{t.description}</p>
                      {isActive && (
                        <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                          <Check className="w-3 h-3 text-primary-foreground" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </CollapsibleSection>

          {/* Change Password */}
          <CollapsibleSection title={t('profile.changePassword')} defaultOpen={false}>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="cp">{t('profile.currentPassword')}</Label>
                <Input id="cp" type="password" value={passwords.current}
                  onChange={e => setPasswords(p => ({ ...p, current: e.target.value }))}
                  autoComplete="current-password" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="np">{t('profile.newPassword')}</Label>
                <Input id="np" type="password" value={passwords.new}
                  onChange={e => setPasswords(p => ({ ...p, new: e.target.value }))}
                  autoComplete="new-password"
                  placeholder={t('profile.minChars')} />
                <p className="text-xs text-muted-foreground">{t('profile.passwordHint')}</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cop">{t('profile.confirmPassword')}</Label>
                <Input id="cop" type="password" value={passwords.confirm}
                  onChange={e => setPasswords(p => ({ ...p, confirm: e.target.value }))}
                  autoComplete="new-password" />
              </div>
              <div className="flex justify-end">
                <Button onClick={changePassword} disabled={changingPw} variant="outline">
                  <Lock className="h-4 w-4 mr-1" />
                  {changingPw ? 'Changing...' : t('profile.changePassword')}
                </Button>
              </div>
            </div>
          </CollapsibleSection>

          {/* Default Codes */}
          <DefaultCodesSection />

          {/* Account Info (read-only) */}
          <CollapsibleSection title={t('profile.accountInfo')} defaultOpen={false}>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {[
                ['User ID', user?.user_id],
                ['Email', user?.email],
                ['User Type', user?.user_type],
                ['Licensed', user?.is_licensed ? 'Yes' : 'No'],
                ['Last Login', user?.last_login ? new Date(user.last_login).toLocaleString('en-US', { timeZone: systemSettings?.timezone || 'America/Los_Angeles' }) : 'N/A'],
                ['Admin', user?.is_admin ? 'Yes' : 'No'],
              ].map(([label, value]) => (
                <div key={label} className="space-y-0.5">
                  <p className="text-xs font-medium text-muted-foreground">{label}</p>
                  <p className="text-sm">{value || '—'}</p>
                </div>
              ))}
            </div>
          </CollapsibleSection>
        </div>
      </div>
    </AppShell>
  );
}


function MySitesDisplay({ userId }) {
  const [sites, setSites] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/api/sites/module/status').catch(() => ({ data: { enabled: false } })),
      api.get('/api/sites').catch(() => ({ data: { data: [] } })),
      api.get('/api/auth/me').catch(() => ({ data: {} })),
    ]).then(([statusRes, sitesRes, meRes]) => {
      setEnabled(statusRes.data.enabled);
      const allSites = sitesRes.data.data || [];
      const mySiteIds = meRes.data?.allowed_sites || [];
      setSites(mySiteIds.length > 0 ? allSites.filter(s => mySiteIds.includes(s.site_id)) : []);
      setLoaded(true);
    });
  }, [userId]);

  if (!loaded || !enabled) return null;

  return (
    <CollapsibleSection title={`My Sites (${sites.length})`}>
      {sites.length === 0 ? (
        <p className="text-xs text-muted-foreground">You are not assigned to any specific sites. Contact your administrator.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {sites.map(s => (
            <div key={s.site_id} className="flex items-center gap-2.5 p-3 rounded-xl border border-primary/20 bg-primary/5">
              <Building2 className="h-4 w-4 text-primary flex-shrink-0" />
              <div>
                <p className="text-sm font-medium">{s.name}</p>
                {s.code && <span className="text-xs text-muted-foreground">({s.code})</span>}
                {s.city && <p className="text-[10px] text-muted-foreground">{s.city}{s.country ? `, ${s.country}` : ''}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="text-[10px] text-muted-foreground mt-2">Site assignments are managed by your administrator.</p>
    </CollapsibleSection>
  );
}


function TwoFactorSetup() {
  const [enabled, setEnabled] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [setting, setSetting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [showDisable, setShowDisable] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get('/api/auth/me').then(r => {
      setEnabled(!!r.data?.totp_enabled);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const startSetup = async () => {
    setSetting(true);
    try {
      const r = await api.post('/api/auth/2fa/setup');
      setQrCode(r.data.qr_code);
      setSecret(r.data.secret);
    } catch (e) { toast.error(getApiErrorMessage(e, 'Setup failed')); }
    setSetting(false);
  };

  const verifySetup = async () => {
    setVerifying(true);
    try {
      await api.post('/api/auth/2fa/verify', { code: verifyCode });
      toast.success('Two-factor authentication enabled!');
      setEnabled(true);
      setQrCode('');
      setSecret('');
      setVerifyCode('');
    } catch (e) { toast.error(getApiErrorMessage(e, 'Verification failed')); }
    setVerifying(false);
  };

  const disable2FA = async () => {
    try {
      await api.post('/api/auth/2fa/disable', { password: disablePassword });
      toast.success('Two-factor authentication disabled');
      setEnabled(false);
      setShowDisable(false);
      setDisablePassword('');
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed')); }
  };

  if (!loaded) return null;

  return (
    <CollapsibleSection title={`Two-Factor Authentication ${enabled ? '(Active)' : ''}`}>
      <div className="space-y-4">
        {enabled ? (
          <div>
            <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-3">
              <Shield className="h-4 w-4" />
              <p className="text-sm font-medium">2FA is active on your account</p>
            </div>
            {!showDisable ? (
              <Button size="sm" variant="outline" onClick={() => setShowDisable(true)}>Disable 2FA</Button>
            ) : (
              <div className="space-y-2 border rounded-lg p-3 bg-red-50/50">
                <p className="text-xs text-muted-foreground">Enter your password to disable two-factor authentication:</p>
                <Input type="password" value={disablePassword} onChange={e => setDisablePassword(e.target.value)} placeholder="Current password" className="max-w-xs" />
                <div className="flex gap-2">
                  <Button size="sm" variant="destructive" onClick={disable2FA} disabled={!disablePassword}>Confirm Disable</Button>
                  <Button size="sm" variant="outline" onClick={() => { setShowDisable(false); setDisablePassword(''); }}>Cancel</Button>
                </div>
              </div>
            )}
          </div>
        ) : qrCode ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Scan this QR code with your authenticator app (Google Authenticator, Microsoft Authenticator, Authy, etc.):</p>
            <div className="flex justify-center">
              <img src={qrCode} alt="2FA QR Code" className="w-48 h-48 border rounded-lg p-2 bg-white" />
            </div>
            <div className="text-center">
              <p className="text-[10px] text-muted-foreground">Can't scan? Enter this key manually:</p>
              <code className="text-xs font-mono bg-muted px-2 py-1 rounded select-all">{secret}</code>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">Enter the 6-digit code from your app to verify:</p>
              <div className="flex items-center gap-2">
                <Input
                  type="text" inputMode="numeric" maxLength={6} value={verifyCode}
                  onChange={e => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000" className="w-40 text-center text-lg font-mono tracking-[0.3em]"
                  data-testid="2fa-setup-code"
                />
                <Button onClick={verifySetup} disabled={verifying || verifyCode.length !== 6}>
                  {verifying ? 'Verifying...' : 'Verify & Enable'}
                </Button>
              </div>
            </div>
            <button className="text-xs text-muted-foreground hover:text-primary" onClick={() => { setQrCode(''); setSecret(''); }}>Cancel setup</button>
          </div>
        ) : (
          <div>
            <p className="text-sm text-muted-foreground mb-3">Add an extra layer of security to your account. You'll need an authenticator app on your phone.</p>
            <Button size="sm" onClick={startSetup} disabled={setting}>
              <Shield className="h-3.5 w-3.5 mr-1" /> {setting ? 'Generating...' : 'Set Up 2FA'}
            </Button>
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
}


function DefaultCodesSection() {
  const [codes, setCodes] = useState({});
  const [segments, setSegments] = useState([]);
  const [fieldOptions, setFieldOptions] = useState({ shipping_methods: [], incoterms: [] });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const { labels: expLabels } = useExpenditureLabels();

  // Map segment numbers to expenditure label keys
  const segLabelMap = { 1: 'exp_code_1', 2: 'exp_code_2', 3: 'exp_code_3', 4: 'exp_code_4', 5: 'exp_code_5', 6: 'exp_code_6', 7: 'cost_centre' };

  useEffect(() => {
    Promise.all([
      api.get('/api/users/me/default-codes'),
      api.get('/api/system/account-code-segments'),
      api.get('/api/orders/field-options').catch(() => ({ data: { shipping_methods: [], incoterms: [] } })),
    ]).then(([codesRes, segsRes, optRes]) => {
      setCodes(codesRes.data.default_codes || {});
      setSegments(segsRes.data || []);
      setFieldOptions(optRes.data || { shipping_methods: [], incoterms: [] });
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const update = (k, v) => setCodes(p => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/api/users/me/default-codes', { default_codes: codes });
      toast.success('Default codes saved');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  if (!loaded) return null;

  return (
    <CollapsibleSection title="My Default Codes" defaultOpen={false}>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5"><Label className="text-xs">Delivery Point</Label>
          <TypeAheadLookup endpoint="/api/data/delivery-points" valueField="code" labelField="name" value={codes.delivery_point || ''} displayValue={codes.delivery_point || ''} placeholder="Search delivery points..." onChange={code => update('delivery_point', code)} /></div>
        <div className="space-y-1.5"><Label className="text-xs">Invoice Point</Label>
          <TypeAheadLookup endpoint="/api/data/invoice-points" valueField="code" labelField="name" value={codes.invoice_point || ''} displayValue={codes.invoice_point || ''} placeholder="Search invoice points..." onChange={code => update('invoice_point', code)} /></div>
        <div className="space-y-1.5"><Label className="text-xs">Document Type</Label>
          <TypeAheadLookup endpoint="/api/data/document-types" valueField="code" labelField="description" value={codes.document_type || ''} displayValue={codes.document_type || ''} placeholder="Search document types..." onChange={code => update('document_type', code)} /></div>
        <div className="space-y-1.5"><Label className="text-xs">Category</Label>
          <TypeAheadLookup endpoint="/api/data/categories" valueField="code" labelField="description" value={codes.category || ''} displayValue={codes.category || ''} placeholder="Search categories..." onChange={code => update('category', code)} /></div>
        {segments.filter(s => s.active).map(seg => {
          const labelKey = segLabelMap[seg.segment_number];
          const label = labelKey ? (expLabels[labelKey] || seg.label) : seg.label;
          return (
            <div key={seg.segment_number} className="space-y-1.5">
              <Label className="text-xs">{label}</Label>
              <TypeAheadLookup endpoint={`/api/account-codes/${seg.segment_number}`} valueField="code" labelField="description" value={codes[`account_code_${seg.segment_number}`] || ''} displayValue={codes[`account_code_${seg.segment_number}`] || ''} placeholder={`Search ${label.toLowerCase()}...`} onChange={code => update(`account_code_${seg.segment_number}`, code)} />
            </div>
          );
        })}
        <div className="space-y-1.5"><Label className="text-xs">Budget</Label>
          <select value={codes.budget || ''} onChange={e => update('budget', e.target.value)} className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm">
            <option value="">Select...</option>
            <option value="IN_BUDGET">In Budget</option>
            <option value="OUT_OF_BUDGET">Out of Budget</option>
            <option value="OVER_BUDGET">Over Budget</option>
          </select></div>
        <div className="space-y-1.5"><Label className="text-xs">Incoterms</Label>
          <select value={codes.incoterms || ''} onChange={e => update('incoterms', e.target.value)} className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm">
            <option value="">Select...</option>
            {fieldOptions.incoterms.map(t => <option key={t} value={t}>{t}</option>)}
          </select></div>
        <div className="space-y-1.5"><Label className="text-xs">Shipping Method</Label>
          <select value={codes.shipping_method || ''} onChange={e => update('shipping_method', e.target.value)} className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm">
            <option value="">Select...</option>
            {fieldOptions.shipping_methods.map(m => <option key={m} value={m}>{m}</option>)}
          </select></div>
      </div>
      <div className="flex justify-end mt-4">
        <Button size="sm" onClick={save} disabled={saving}><Save className="h-3.5 w-3.5 mr-1" /> {saving ? 'Saving...' : 'Save Defaults'}</Button>
      </div>
    </CollapsibleSection>
  );
}
